import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useAccountingShouldDoFirstConnection from '~/hooks/useAccountingShouldDoFirstConnection';
import { useHasAutomaticReconciliation } from '~/hooks/useFeatureFlag';
import useRouteMatch from '~/hooks/useRouteMatch';
import CalendarDeadlineIcon from '~/icons/icon-calendar-deadline.svg?react';
import { RouteConfig } from '~/routes/routeConfig';
import { useIsSubmenuOpen, useSetSubmenu } from '~/state/navigationSidebar';
import { SubmenuItem, SubmenuSection } from '~/types/sidebar';
import NavigationSidebarItem from '../NavigationSidebarItem';

interface Props {
  disabled?: boolean;
}

const OutstandingItem: React.FC<Props> = ({ disabled }) => {
  const { t } = useTranslation('sidebar');
  const isSubmenuOpen = useIsSubmenuOpen('outstanding');
  const openSubmenu = useSetSubmenu('outstanding');
  const automaticReconciliationFlag = useHasAutomaticReconciliation();
  const shouldDoFirstConnection = useAccountingShouldDoFirstConnection();

  const sections: SubmenuSection[] = useMemo(() => {
    const result: SubmenuSection[] = [];

    const overviewItems = [
      {
        label: t('outstanding.submenu.dashboard'),
        href: RouteConfig.OutstandingSummary.buildLink(),
      },
      {
        label: t('outstanding.submenu.deadlines'),
        href: RouteConfig.OutstandingFlows.buildLink(),
        matchSubRoutes: true,
      },
    ];

    overviewItems.push({
      label: t('outstanding.submenu.automatic_payment_reminders'),
      href: RouteConfig.OutstandingAutomaticPaymentReminders.buildLink(),
    });

    result.push({
      label: t('outstanding.submenu.overview'),
      items: overviewItems,
    });

    const manageItems: SubmenuItem[] = [
      {
        label: t('outstanding.submenu.recurring_payment'),
        href: RouteConfig.Recurrences.buildLink(),
        matchSubRoutes: true,
      },
    ];

    if (automaticReconciliationFlag)
      manageItems.push({
        label: t('outstanding.submenu.rules'),
        href: RouteConfig.OutstandingRules.buildLink(),
        matchSubRoutes: true,
      });

    result.push({
      label: t('outstanding.submenu.manage'),
      items: manageItems,
    });

    return result;
  }, [t, automaticReconciliationFlag]);

  const isActive = Boolean(useRouteMatch(sections.flatMap((s) => s.items.map((i) => i.href ?? ''))));

  return (
    <NavigationSidebarItem
      href={RouteConfig.Outstanding.buildLink()}
      iconComponent={CalendarDeadlineIcon}
      isSubmenuOpen={isSubmenuOpen}
      disabled={disabled}
      onOpenSubmenu={openSubmenu}
      title={t('outstanding.label')}
      submenuSections={shouldDoFirstConnection === 'first_connection' ? undefined : sections}
      isActive={isActive}
      notificationCount={shouldDoFirstConnection === 'first_connection' ? 1 : undefined}
    />
  );
};

export default memo(OutstandingItem);
